import axios from 'axios';
import {
  ConnectionState,
  OAuthInstallParams,
  ShopifyInstallParams,
  PlatformConnectionApi,
  StorefrontApi,
  StorefrontState,
} from '../../components/platform-setup/types';
import {
  CatalogEntry,
  ExpectedGoLive,
  MappingRow,
  MappingState,
  MappingSummary,
  MigrationHistoryItem,
  MigrationV2,
  NewCatalogProduct,
} from './types';

// O interceptor do axios já põe /:customer no baseURL
const base = (id: string) => `/platform-migration/${id}`;

// O cliente vai no caminho: o app da Shopify é criado por loja, e o retorno não pode depender
// do cliente ativo do navegador (o usuário pode ter trocado de cliente em outra aba)
export const shopifyCallbackPath = (customer: string) => `/platform-migration/shopify-callback/${encodeURIComponent(customer)}`;

// App URL fixa do app Shopify da loja (aberta depois da instalação e sempre que o lojista abre o
// app no admin): decide entre a migração, a renovação do token da loja no ar e o painel
export const shopifyAppPath = (customer: string) => `/shopify/app/${encodeURIComponent(customer)}`;

// Tray e Nuvemshop: o retorno leva o cliente e o state da migração. Na Tray vão no caminho do
// callback (o auth.php acrescenta ?code&store); na Nuvemshop o redirect é fixo no app
// (/nuvemshop/callback), então vão no parâmetro state
export const oauthCallbackPath = (platform: string, customer: string, state: string) =>
  `/platform-migration/oauth-callback/${platform}/${encodeURIComponent(customer)}/${encodeURIComponent(state)}`;

const NUVEMSHOP_STATE_PREFIX = 'pm';

export const nuvemshopMigrationState = (customer: string, state: string) => [NUVEMSHOP_STATE_PREFIX, customer, state].join('~');

/** { customer, state } quando o state da Nuvemshop é de uma migração; null no fluxo antigo */
export const parseNuvemshopMigrationState = (raw: string | null): { customer: string; state: string } | null => {
  const parts = (raw || '').split('~');
  if (parts.length !== 3 || parts[0] !== NUVEMSHOP_STATE_PREFIX || !parts[1] || !/^[a-f0-9]{48}$/.test(parts[2])) return null;
  return { customer: parts[1], state: parts[2] };
};

const oauthAuthorizeUrlFor = (customer: string) => (install: OAuthInstallParams, storeOrigin?: string) => {
  if (install.platform === 'tray') {
    const callback = window.location.origin + oauthCallbackPath('tray', customer, install.state);
    return `${storeOrigin}/auth.php?response_type=code&consumer_key=${encodeURIComponent(install.appId)}&callback=${encodeURIComponent(callback)}`;
  }
  return `https://www.nuvemshop.com.br/apps/${encodeURIComponent(install.appId)}/authorize?state=${encodeURIComponent(
    nuvemshopMigrationState(customer, install.state)
  )}`;
};

// Chamada com o cliente explícito, ignorando o cliente ativo que o interceptor põe no baseURL
const explicitCustomerUrl = (customer: string, path: string) =>
  `${process.env.REACT_APP_API_URL}/${encodeURIComponent(customer)}${path}`;

export const migrationApi = {
  async getActive(): Promise<{ migration: MigrationV2 | null; history: MigrationHistoryItem[] }> {
    const { data } = await axios.get('/platform-migration');
    return { migration: data.migration || null, history: data.history || [] };
  },
  async getConfigs(): Promise<{ platforms: CatalogEntry[]; current: CatalogEntry | null }> {
    const { data } = await axios.get('/platform-migration/configs');
    return { platforms: data.platforms || [], current: data.current || null };
  },
  async create(targetPlatform: string): Promise<MigrationV2> {
    const { data } = await axios.post('/platform-migration', { targetPlatform, wizardVersion: 2 });
    return data.migration;
  },
  async setStart(id: string, input: { mode?: 'self' | 'konfidency'; expectedGoLive?: ExpectedGoLive }): Promise<MigrationV2> {
    const { data } = await axios.put(`${base(id)}/start`, input);
    return data.migration;
  },
  async cancel(id: string): Promise<void> {
    await axios.delete(base(id));
  },
  async startCatalogImport(id: string): Promise<MigrationV2> {
    const { data } = await axios.post(`${base(id)}/catalog/import`);
    return data.migration;
  },
  async getTempProductCount(id: string): Promise<number> {
    const { data } = await axios.get(`${base(id)}/temp-products`, { params: { limit: 1 } });
    return data.total || 0;
  },
  async searchCatalog(id: string, search: string): Promise<NewCatalogProduct[]> {
    const { data } = await axios.get(`${base(id)}/catalog`, { params: { search, limit: 20 } });
    return data.products || [];
  },
  async getMappingSummary(id: string): Promise<MappingSummary> {
    const { data } = await axios.get(`${base(id)}/mapping/summary`);
    return data;
  },
  async listMapping(
    id: string,
    params: { priority: 'content' | 'catalog'; state?: MappingState; search?: string; page: number; pageSize: number }
  ): Promise<{ rows: MappingRow[]; total: number }> {
    const { data } = await axios.get(`${base(id)}/mapping`, { params });
    return data;
  },
  async updateMappingRow(id: string, rowId: string, body: { action: 'accept' | 'select' | 'none'; newSku?: string }): Promise<MappingRow> {
    const { data } = await axios.patch(`${base(id)}/mapping/${rowId}`, body);
    return data.row;
  },
  async acceptSuggestions(id: string, minScore = 90): Promise<{ accepted: number }> {
    const { data } = await axios.post(`${base(id)}/mapping/accept-suggestions`, { minScore });
    return data;
  },
  async exportMapping(id: string, customer: string): Promise<void> {
    const response = await axios.get(`${base(id)}/mapping/export`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `correspondencia-${customer}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  async importMapping(id: string, file: File): Promise<{ applied: number; errorCount: number; errors: { row: number; oldSku: string; code: string }[] }> {
    const form = new FormData();
    form.append('file', file);
    const { data } = await axios.post(`${base(id)}/mapping/import`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
    return data;
  },
  async getActiveFor(customer: string): Promise<MigrationV2 | null> {
    const { data } = await axios.get(explicitCustomerUrl(customer, '/platform-migration'));
    return data.migration || null;
  },
  async completeShopifyInstall(customer: string, id: string, params: Record<string, string>): Promise<ConnectionState> {
    const { data } = await axios.post(explicitCustomerUrl(customer, `${base(id)}/connection/shopify/authorize`), { params });
    return data;
  },
  async getShopifyInstallFor(customer: string, id: string): Promise<ShopifyInstallParams> {
    return (await axios.get(explicitCustomerUrl(customer, `${base(id)}/connection/shopify/install`))).data;
  },
  async completeOAuthInstall(customer: string, id: string, body: { state: string; code: string | null; store?: string | null }): Promise<ConnectionState> {
    const { data } = await axios.post(explicitCustomerUrl(customer, `${base(id)}/connection/oauth/authorize`), body);
    return data;
  },
};

export type LegacyTrigger = 'eta' | 'approval' | 'cutover' | 'none';

export interface CutoverChecklistItem {
  id: string;
  ok: boolean;
  step: string;
  applies?: boolean;
  detail?: { pending?: number; products?: number; missing?: string[]; manual?: boolean };
}

export interface CutoverState {
  status: string;
  checklist: CutoverChecklistItem[];
  ready: boolean;
  changes: { reviews: number; questions: number };
  legacyInvites: {
    status: 'missing' | 'building' | 'ready' | 'failed';
    orders: number;
    invites: number;
    channels: string[];
    choice?: { trigger: LegacyTrigger; days: number };
    scheduled?: number;
    firstDueAt?: string;
    lastDueAt?: string;
  };
  execution: {
    steps: Record<string, { status: 'running' | 'done' | 'failed' }>;
    currentStep?: string;
    failedStep?: string;
    errorCode?: string;
    completedAt?: string;
  };
}

export interface LegacyPreview {
  status: string;
  orders: number;
  invites: number;
  noEta: number;
  pastDue: number;
  firstDueAt?: string | null;
  lastDueAt?: string | null;
}

export const cutoverApi = {
  async getCutover(id: string): Promise<CutoverState> {
    return (await axios.get(`${base(id)}/cutover`)).data;
  },
  async previewLegacy(id: string, trigger: LegacyTrigger, days: number): Promise<LegacyPreview> {
    return (await axios.get(`${base(id)}/cutover/legacy-preview`, { params: { trigger, days } })).data;
  },
  async refreshLegacy(id: string): Promise<CutoverState> {
    return (await axios.post(`${base(id)}/cutover/legacy/refresh`)).data;
  },
  async startCutover(id: string, legacy: { trigger: LegacyTrigger; days: number }): Promise<CutoverState> {
    return (await axios.post(`${base(id)}/cutover`, { legacy, confirmed: true })).data;
  },
  async retryCutover(id: string): Promise<CutoverState> {
    return (await axios.post(`${base(id)}/cutover/retry`)).data;
  },
};

/** Adaptador do painel de conexão compartilhado para as rotas da migração */
export const connectionApiFor = (id: string, customer: string): PlatformConnectionApi => ({
  getState: async () => (await axios.get(`${base(id)}/connection`)).data,
  saveCredentials: async (input) => (await axios.put(`${base(id)}/connection/credentials`, input)).data,
  saveStoreUrl: async (storeUrl) => (await axios.put(`${base(id)}/connection/store-url`, { storeUrl })).data,
  validate: async () => (await axios.post(`${base(id)}/connection/validate`)).data,
  getVtexSalesChannels: async () => (await axios.get(`${base(id)}/connection/vtex/sales-channels`)).data.channels,
  saveVtexOrderSetup: async (input) => (await axios.put(`${base(id)}/connection/vtex/order-setup`, input)).data,
  requestShopifyApp: async (shopDomain) => (await axios.post(`${base(id)}/connection/shopify/request-app`, { shopDomain })).data,
  getShopifyInstall: async () => (await axios.get(`${base(id)}/connection/shopify/install`)).data,
  registerShopifyApp: async (input) => (await axios.put(`${base(id)}/shopify-app`, input)).data,
  shopifyCallbackPath: shopifyCallbackPath(customer),
  shopifyAppPath: shopifyAppPath(customer),
  getOAuthInstall: async () => (await axios.get(`${base(id)}/connection/oauth/install`)).data,
  oauthAuthorizeUrl: oauthAuthorizeUrlFor(customer),
  completeTrayManual: async (url) => (await axios.post(`${base(id)}/connection/tray/manual`, { url })).data,
  getWakeOrderStatuses: async () => (await axios.get(`${base(id)}/connection/wake/order-statuses`)).data,
  saveWakeOrderSetup: async (wakeStatusMapping) => (await axios.put(`${base(id)}/connection/wake/order-statuses`, { wakeStatusMapping })).data,
});

/** Adaptador do painel da loja compartilhado para as rotas da migração */
export const storefrontApiFor = (id: string): StorefrontApi => ({
  getState: async (): Promise<StorefrontState> => (await axios.get(`${base(id)}/storefront`)).data,
  saveChoices: async (input): Promise<StorefrontState> => (await axios.put(`${base(id)}/storefront`, input)).data,
  installScript: async (): Promise<StorefrontState> => (await axios.post(`${base(id)}/storefront/install-script`)).data,
  issueSetupToken: async () => (await axios.post(`${base(id)}/storefront/setup-token`)).data,
});
